const DB_NAME = 'main';
const DB_VERSION = 1;
const STORE_NAME = 'cacheScreens';

// `indexedDB.open` can hang indefinitely: another tab holding an older version
// open fires `onblocked` and the request simply never settles. Every read below
// is `yield call(...)`ed from a saga, so an open that never settles is a
// navigation that never completes. Bound it and treat the timeout as a miss.
const OPEN_TIMEOUT_MS = 3000;

// One connection for the page, not one per operation. The old code opened a
// fresh connection on every call and never closed any of them, which widens the
// window in which a future version bump is blocked by our own stale handles.
let dbPromise: Promise<IDBDatabase> | null = null;

const logFailure = (operation: string, details: Record<string, unknown>, error: unknown) => {
  const err = error as { message?: string; name?: string } | null;
  console.error(`IndexedDB ${operation} failed:`, {
    ...details,
    error: err?.message || error,
    errorName: err?.name,
    timestamp: new Date().toISOString()
  });
};

function openDatabase(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    if (typeof indexedDB === 'undefined' || indexedDB === null) {
      reject(new Error('IndexedDB is unavailable in this context'));
      return;
    }

    // A private window can throw synchronously from `open` itself.
    let request: IDBOpenDBRequest;
    try {
      request = indexedDB.open(DB_NAME, DB_VERSION);
    } catch (error) {
      reject(error);
      return;
    }

    let settled = false;
    let timer: ReturnType<typeof setTimeout>;
    const finish = (fn: (arg?: any) => void, arg?: any) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      fn(arg);
    };
    timer = setTimeout(() => finish(reject, new Error('IndexedDB open timed out')), OPEN_TIMEOUT_MS);

    request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
      }
    };

    // Another tab is holding an older version open. Fail fast rather than wait:
    // the caller degrades to a cache miss and goes to the network, which is a
    // far better outcome than a navigation that hangs until the other tab closes.
    request.onblocked = () => finish(reject, new Error('IndexedDB upgrade blocked by another tab'));

    request.onsuccess = () => {
      const db = request.result;
      // Never be the tab that blocks someone else's upgrade.
      db.onversionchange = () => {
        db.close();
        dbPromise = null;
      };
      db.onclose = () => {
        dbPromise = null;
      };
      finish(resolve, db);
    };

    request.onerror = () => finish(reject, request.error);
  }).catch((error) => {
    logFailure('openDatabase', { dbName: DB_NAME, dbVersion: DB_VERSION }, error);
    dbPromise = null;
    throw error;
  });

  return dbPromise;
}

/**
 * Runs one transaction and NEVER rejects.
 *
 * Callers here are not equipped to handle a rejection. `setItem` is invoked
 * fire-and-forget from a dozen saga sites, so a rejection surfaces as an
 * unhandled promise rejection; and the `getItem` at `sagas.ts:476` sits OUTSIDE
 * the try that opens at `:500`, so a rejection there aborts the whole form
 * submission — which is exactly what happens today in a private window.
 * Every failure degrades to `fallback`, i.e. a cache miss.
 */
async function withStore<T>(
  operation: string,
  mode: IDBTransactionMode,
  details: Record<string, unknown>,
  fallback: T,
  run: (store: IDBObjectStore, resolve: (value: T) => void, reject: (error: unknown) => void) => void
): Promise<T> {
  try {
    const db = await openDatabase();
    return await new Promise<T>((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], mode);
      transaction.onabort = () => reject(transaction.error);
      run(transaction.objectStore(STORE_NAME), resolve, reject);
    });
  } catch (error) {
    logFailure(operation, details, error);
    // The handle may be the thing that went bad (closed connection, evicted
    // database). Drop it so the next call re-opens rather than failing forever.
    dbPromise = null;
    return fallback;
  }
}

export async function setItem(key: string, value: any): Promise<void> {
  return withStore<void>('setItem', 'readwrite', { key }, undefined, (store, resolve, reject) => {
    const request = store.put({ id: key, value });
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

export async function removeItem(key: string): Promise<void> {
  return withStore<void>('removeItem', 'readwrite', { key }, undefined, (store, resolve, reject) => {
    const request = store.delete(key);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

export async function getItem(key: string): Promise<any> {
  return withStore<any>('getItem', 'readonly', { key }, [null, null], (store, resolve, reject) => {
    const request = store.get(key);
    request.onsuccess = () => {
      const result = request.result;
      resolve([result?.id, result?.value]);
    };
    request.onerror = () => reject(request.error);
  });
}

/**
 * Drops every cached screen. Used when the active theme changes: cached bodies
 * store their `screen.sections` ALREADY token-substituted, so they carry the
 * values of whichever theme was live when they were written. Worst case here is
 * a cold cache, which is the cheapest possible failure.
 */
export async function clear(): Promise<void> {
  return withStore<void>('clear', 'readwrite', {}, undefined, (store, resolve, reject) => {
    const request = store.clear();
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

export async function getItemRegexp(command: string): Promise<any> {
  return withStore<any>('getItemRegexp', 'readonly', { command }, [null, null], (store, resolve, reject) => {
    const request = store.getAll(); // Fetch all entries to apply regex matching

    request.onsuccess = () => {
      const entries = request.result;
      const matches: { key: string; value: any; specificity: number }[] = [];

      for (const entry of entries) {
        const key = entry.id;
        const value = entry.value;

        if (!key) continue;

        // Check for exact match first
        if (key === command) {
          matches.push({ key, value, specificity: -1 }); // Highest priority for exact matches
          continue;
        }

        // Check if the command is a prefix match for the key (before any placeholders)
        const placeholderIndex = key.indexOf('{{');
        if (placeholderIndex !== -1) {
          const keyPrefix = key.substring(0, placeholderIndex);
          if (command === keyPrefix) {
            // The command matches the key up to the first placeholder
            const specificity = (key.match(/{{\w+}}/g) || []).length;
            matches.push({ key, value, specificity });
            continue;
          }
        }

        // Escape special regex characters in the key
        const escapedKey = key.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');

        // Replace {{...}} placeholders with regex patterns
        const pattern = escapedKey.replace(/{{\w+}}/g, '[\\w\\-\\.~\\?&=]*'); // Make placeholders optional
        const regex = new RegExp(`^${pattern}$`);

        // Check if the command matches the regex
        if (regex.test(command)) {
          // Calculate specificity: fewer wildcards = higher specificity
          const specificity = (key.match(/{{\w+}}/g) || []).length;
          matches.push({ key, value, specificity });
        }
      }

      if (matches.length > 0) {
        // Sort matches by specificity (ascending: fewer wildcards = higher priority)
        matches.sort((a, b) => a.specificity - b.specificity);
        // Resolve with the best match
        resolve([matches[0].key, matches[0].value]);
      } else {
        // If no match is found, resolve with null
        resolve([null, null]);
      }
    };

    request.onerror = () => reject(request.error);
  });
}
